<template>
  <div class="product-card" :class="{ featured: product.featured, 'is-bundle': isBundle || isGrouped }">

    <!-- Image area -->
    <router-link :to="`/product/${product.slug}`" class="product-image">
      <!-- LQIP: piepkleine geblurde placeholder, fade weg als de scherpe foto er is -->
      <img
        v-if="lqipSrc"
        :src="lqipSrc"
        alt=""
        aria-hidden="true"
        class="lqip"
        :class="{ hidden: imgLoaded }"
      >
      <img
        ref="mainImgEl"
        :src="productImage"
        :alt="product.name"
        :loading="index < 4 ? 'eager' : 'lazy'"
        :fetchpriority="index === 0 ? 'high' : 'auto'"
        class="main-img"
        @load="imgLoaded = true"
        @error="imgLoaded = true"
      >

      <!-- Badges top-left -->
      <div class="badges-stack">
        <span v-if="bestseller" class="badge bestseller-badge"><i class="bi bi-fire"></i> Bestseller</span>
        <span v-if="isBundle || isGrouped" class="badge bundle-badge"><i class="bi bi-box-seam"></i> Complete Set</span>
        <span v-if="product.on_sale && savingsPercentage > 0" class="badge sale-badge">-{{ savingsPercentage }}%</span>
        <span v-if="isNewProduct" class="badge new-badge">Nieuw</span>
        <!-- Speciale product-labels vanuit WooCommerce tags -->
        <span
          v-for="label in productLabels"
          :key="label.slug"
          class="badge"
          :style="`background:${label.bg};color:${label.color};border:none`"
        >
          <i v-if="label.icon" :class="['bi', label.icon]" style="margin-right:3px;font-size:.65rem"></i>{{ label.text }}
        </span>
      </div>

      <!-- Bundle strip onderaan afbeelding -->
      <div v-if="isBundle || isGrouped" class="bundle-image-strip">
        <i class="bi bi-check2-circle"></i> Alles inbegrepen
      </div>

      <!-- Wishlist top-right -->
      <button
        class="btn-wishlist-overlay"
        :class="{ active: isInWishlist }"
        @click.prevent="toggleWishlist"
        title="Verlanglijst"
      >
        <i :class="['bi', isInWishlist ? 'bi-heart-fill' : 'bi-heart']"></i>
      </button>

      <!-- Quick-view hover -->
      <div class="image-overlay">
        <span class="btn-quick-view-overlay">
          <i class="bi bi-eye"></i> Bekijken
        </span>
      </div>
    </router-link>

    <!-- Body -->
    <div class="product-body">

      <!-- Category -->
      <span v-if="productCategory" class="product-category">{{ productCategory }}</span>

      <!-- Title -->
      <h3 class="product-title">
        <router-link :to="`/product/${product.slug}`">{{ product.name }}</router-link>
      </h3>

      <!-- Stars -->
      <div v-if="product.average_rating > 0" class="product-rating-section">
        <div class="stars">
          <i v-for="s in 5" :key="s" :class="['bi', s <= Math.round(product.average_rating) ? 'bi-star-fill' : 'bi-star']"></i>
        </div>
        <span class="rating-text">{{ formatRating(product.average_rating) }}</span>
        <span v-if="product.rating_count > 0" class="rating-count">({{ product.rating_count }})</span>
      </div>

      <!-- USPs met vinkjes (uit custom veld of site-brede standaard) -->
      <ul class="usps">
        <template v-if="productUsps">
          <li v-for="usp in productUsps" :key="usp"><i class="bi bi-check2"></i> {{ usp }}</li>
        </template>
        <template v-else>
          <li><i class="bi bi-check2"></i> Morgen in huis</li>
          <li><i class="bi bi-check2"></i> 2 jaar garantie</li>
          <li v-if="hasFreeShipping"><i class="bi bi-check2"></i> Gratis verzending</li>
        </template>
      </ul>

      <!-- Price -->
      <div class="product-price-section">
        <span v-if="isGrouped" class="text-[0.72rem] text-[#aaa] font-medium mr-1">Vanaf</span>
        <span class="price-current">{{ formatPrice(currentPrice) }}</span>
        <span v-if="!isGrouped && !isBundle && product.on_sale && regularPrice" class="price-old">{{ formatPrice(regularPrice) }}</span>
        <span v-if="!isGrouped && !isBundle && savingsPercentage > 0" class="savings-pill">-{{ savingsPercentage }}%</span>
        <span v-if="isGrouped || isBundle" class="set-pill">Complete set</span>
      </div>

      <!-- Excl. BTW -->
      <div v-if="!isGrouped && !isBundle && currentPrice && !isZeroVat" class="excl-btw-row">
        Excl. BTW: {{ formatPrice(exclBtwPrice) }}
      </div>

      <!-- Levertijd badge -->
      <div v-if="deliveryTime" class="delivery-time-badge" :style="`color:${deliveryTime.color}`">
        <i :class="['bi', deliveryTime.icon]"></i>
        {{ deliveryTime.label }}
      </div>

      <!-- Low stock warning -->
      <div v-if="isLowStock" class="stock-warning">
        <i class="bi bi-fire"></i> Nog {{ stockQuantity }} op voorraad
      </div>

      <!-- Op voorraad indicator -->
      <div v-if="(product.stock_status === 'instock' || product.backorders_allowed) && !isLowStock && !isBundle && !isGrouped" class="in-stock-row">
        <span class="stock-dot">●</span> Op voorraad
      </div>

      <!-- Uitverkocht: voorraad-melding -->
      <div v-if="product.stock_status === 'outofstock' && !product.backorders_allowed && !isBundle && !isGrouped" @click.stop>
        <StockNotify
          :productId="product.id"
          :productName="product.name"
          :productSlug="product.slug"
          :productImage="productImage"
          :compact="true"
        />
      </div>

      <!-- Add to cart (op voorraad of bundle/grouped) -->
      <button
        v-else
        @click="addToCart"
        class="btn-add-to-cart"
        :disabled="isAddingToCart"
      >
        <i :class="['bi', isAddingToCart ? 'bi-arrow-repeat spin' : (isBundle || isGrouped) ? 'bi-arrow-right-circle' : 'bi-cart']"></i>
        {{ addToCartText }}
      </button>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useCartStore } from '@stores/cart'
import { useWishlistStore } from '@stores/wishlist'
import { useRouter } from 'vue-router'
import StockNotify from '@components/StockNotify.vue'
import { useAnalytics } from '@composables/useAnalytics'

const props = defineProps({
  product:  { type: Object, required: true },
  // index 0 = eerste zichtbare kaart → LCP-kandidaat: eager laden + fetchpriority high
  index:    { type: Number, default: 99 },
  listName: { type: String, default: 'Productoverzicht' },
  bestseller: { type: Boolean, default: false },
})

const cartStore     = useCartStore()
const wishlistStore = useWishlistStore()
const router        = useRouter()
const { trackSelectItem, trackAddToCart, trackWishlist } = useAnalytics()
const isAddingToCart = ref(false)

const isInWishlist = computed(() => wishlistStore.isInWishlist(props.product.id))

const productImage = computed(() => {
  const images = props.product.images || []
  if (images.length > 0 && images[0].src) return images[0].src
  if (props.product.thumbnail) return props.product.thumbnail
  return '/placeholder-product.jpg'
})

// LQIP: een 32px-versie via de bestaande media-proxy (?w=32), CSS-geblurd.
// Alleen voor geproxyde /wp-content-afbeeldingen; niet voor de lokale placeholder.
const lqipSrc = computed(() => {
  const src = productImage.value
  if (!src || !src.startsWith('/wp-content/')) return ''
  return src.includes('?') ? `${src}&w=32` : `${src}?w=32`
})

// SSR-veilig: main-img blijft altijd zichtbaar; we faden alleen de blur-laag weg.
const imgLoaded  = ref(false)
const mainImgEl  = ref(null)


const productCategory = computed(() => props.product.categories?.[0]?.name || null)
const productUsps     = computed(() => props.product.usps || null)
const isGrouped    = computed(() => props.product.type === 'grouped')
const isBundle     = computed(() => props.product.is_bundle === true)
const currentPrice = computed(() => props.product.sale_price || props.product.price || props.product.regular_price)
const regularPrice = computed(() => props.product.regular_price)

const savingsAmount = computed(() => {
  if (props.product.on_sale && regularPrice.value && currentPrice.value) {
    return parseFloat(regularPrice.value) - parseFloat(currentPrice.value)
  }
  return 0
})

const savingsPercentage = computed(() => {
  if (savingsAmount.value > 0 && regularPrice.value) {
    return Math.round((savingsAmount.value / parseFloat(regularPrice.value)) * 100)
  }
  return 0
})

const isNewProduct = computed(() => {
  if (!props.product.date_created) return false
  const days = Math.floor((Date.now() - new Date(props.product.date_created)) / 86400000)
  return days <= 30
})

const hasFreeShipping = computed(() => parseFloat(currentPrice.value) >= 50)
const stockQuantity = computed(() => props.product.stock_quantity || 0)
const isZeroVat    = computed(() => props.product.tax_class === 'zero-rate')
const exclBtwPrice = computed(() => currentPrice.value ? parseFloat(currentPrice.value) / 1.21 : 0)
const showStockBadge = computed(() => props.product.manage_stock && (stockQuantity.value <= 5 || !props.product.in_stock))
const isLowStock = computed(() => props.product.stock_status === 'instock' && stockQuantity.value > 0 && stockQuantity.value <= 3)

// ── Product labels (vanuit WooCommerce product-tags) ──────────────────────────
const LABEL_TAGS = {
  'keuze-van-plug2charge': { text: 'Keuze van Plug2Charge', bg: '#FFD500', color: '#111', icon: 'bi-star-fill' },
  'bestseller':            { text: 'Bestseller',            bg: '#111',    color: '#fff', icon: 'bi-trophy-fill' },
  'populair':              { text: 'Populair',              bg: '#f97316', color: '#fff', icon: 'bi-fire' },
  'actie':                 { text: 'Actie',                 bg: '#ef4444', color: '#fff', icon: 'bi-tag-fill' },
  'limited':               { text: 'Limited',              bg: '#8b5cf6', color: '#fff', icon: 'bi-lightning-charge-fill' },
}
const productLabels = computed(() =>
  (props.product.tags || [])
    .map(t => LABEL_TAGS[t.slug] ? { ...LABEL_TAGS[t.slug], slug: t.slug } : null)
    .filter(Boolean)
    .slice(0, 1) // max 1 label per kaart
)

// ── Levertijd ─────────────────────────────────────────────────────────────────
const deliveryTime = ref(null)

onMounted(async () => {
  // Afbeelding kan al geladen zijn vóór hydratie → dan is @load al gemist.
  if (mainImgEl.value?.complete) imgLoaded.value = true
  try {
    const res = await fetch(`/_delivery-time/${props.product.id}`)
    if (res.ok) deliveryTime.value = await res.json()
  } catch {}
})

const isInStock = computed(() => props.product.stock_status === 'instock' || props.product.backorders_allowed)

const stockBadgeClass = computed(() => {
  if (!isInStock.value) return 'out-of-stock'
  if (stockQuantity.value > 0 && stockQuantity.value <= 5) return 'low-stock'
  return 'in-stock'
})

const stockIconClass = computed(() => {
  if (!isInStock.value) return 'bi bi-x'
  if (stockQuantity.value > 0 && stockQuantity.value <= 5) return 'bi bi-fire'
  return 'bi bi-check-circle'
})

const stockText = computed(() => {
  if (!isInStock.value) return 'Uitverkocht'
  if (stockQuantity.value > 0 && stockQuantity.value <= 5) return `${stockQuantity.value} op voorraad`
  return 'Op voorraad'
})

const addToCartText = computed(() => {
  if (isGrouped.value || isBundle.value) return 'Bekijk set'
  if (isAddingToCart.value) return 'Toevoegen...'
  if (props.product.stock_status === 'outofstock' && !props.product.backorders_allowed) return 'Uitverkocht'
  return 'In winkelwagen'
})

import { formatPrice } from '@/utils/price'

const formatRating = (rating) => parseFloat(rating).toFixed(1)

const addToCart = async () => {
  // Grouped/bundle → doorsturen naar productpagina
  if (isGrouped.value || isBundle.value) {
    trackSelectItem(props.product, props.listName, props.index)
    return router.push(`/product/${props.product.slug}`)
  }
  if (isAddingToCart.value || (props.product.stock_status === 'outofstock' && !props.product.backorders_allowed)) return
  try {
    isAddingToCart.value = true
    await cartStore.addToCart(props.product, 1)
    trackAddToCart(props.product, 1, 'product-card')
  } catch (error) {
    console.error('Failed to add to cart:', error)
  } finally {
    isAddingToCart.value = false
  }
}

const quickView = () => {
  trackSelectItem(props.product, props.listName, props.index)
  router.push(`/product/${props.product.slug}`)
}

const toggleWishlist = () => {
  const inWishlist = wishlistStore.isInWishlist(props.product.id)
  wishlistStore.toggleWishlist(props.product)
  trackWishlist(props.product, inWishlist ? 'remove' : 'add')
}
</script>

<style scoped>
/* ---- Card shell ---- */
.product-card {
  position: relative;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.15s ease;
  border: 1px solid #e9eaec;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 10px rgba(17, 17, 17, 0.06);
  border-color: #FFD500;
}

.product-card.featured {
  border-color: #FFD500;
}

.product-card.is-bundle {
  border-color: #FFD500;
}

.product-card.is-bundle:hover {
  border-color: #ffb800;
  box-shadow: 0 2px 10px rgba(17, 17, 17, 0.08);
}

/* ---- Badges (top-left) ---- */
.badges-stack {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.sale-badge   { background: #ff4757; color: #fff; }
.new-badge    { background: #FFD500; color: #000; }
.bestseller-badge { background: #111; color: #FFD500; gap: 4px; }
.bestseller-badge i { font-size: 0.7rem; }
.bundle-badge {
  background: #111;
  color: #FFD500;
  display: flex;
  align-items: center;
  gap: 4px;
}
.bundle-badge i { font-size: 0.72rem; }

/* ---- Wishlist overlay (top-right on image) ---- */
.btn-wishlist-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 11;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #bbb;
  font-size: 1rem;
  transition: color 0.15s ease, background 0.15s ease;
  backdrop-filter: blur(4px);
}

.btn-wishlist-overlay:hover { color: #FFD500; }
.btn-wishlist-overlay.active { color: #FFD500; background: #fff; }

/* ---- Image area ---- */
.product-image {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #fff;
  overflow: hidden;
}

.product-image img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 1rem;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image img {
  transform: scale(1.04);
}

/* LQIP blur-up: geblurde 32px-placeholder onder de scherpe foto, fade weg op load */
.product-image .lqip {
  z-index: 1;
  filter: blur(14px);
  transform: scale(1.06);
  opacity: 1;
  transition: opacity 0.45s ease;
}
.product-image .lqip.hidden {
  opacity: 0;
}
.product-image .main-img {
  z-index: 2;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.product-card:hover .image-overlay { opacity: 1; }

.btn-quick-view-overlay {
  background: #FFD500;
  color: #000;
  padding: 9px 18px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8125rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transform: translateY(6px);
  transition: transform 0.2s ease;
}

.product-card:hover .btn-quick-view-overlay { transform: translateY(0); }

/* ---- Bundle image strip ---- */
.bundle-image-strip {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #FFD500;
  color: #111;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 5px 10px;
  display: flex;
  align-items: center;
  gap: 5px;
  z-index: 5;
}

.bundle-image-strip i { font-size: 0.8rem; }

/* ---- Body ---- */
.product-body {
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
  background: #fff;
}

.product-category {
  font-size: 0.65rem;
  font-weight: 600;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.35;
  min-height: 2.5em;
}

.product-title a { color: #111; text-decoration: none; }
.product-title a:hover { color: #b89b00; }

/* ---- Rating ---- */
.product-rating-section {
  display: flex;
  align-items: center;
  gap: 5px;
}

.stars { display: flex; gap: 2px; }
.stars i              { font-size: 0.72rem; color: #ddd; }
.stars i.bi-star-fill { color: #FFD500; }

.rating-text  { font-size: 0.78rem; font-weight: 600; color: #111; }
.rating-count { font-size: 0.72rem; color: #aaa; }

/* ---- USPs ---- */
.usps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.usps li {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  color: #555;
}

.usps li i {
  font-size: 0.8rem;
  color: #22c55e;
  flex-shrink: 0;
}

/* ---- Price ---- */
.product-price-section {
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: baseline;
  gap: 7px;
  flex-wrap: wrap;
}

.price-current {
  font-size: 1.2rem;
  font-weight: 700;
  color: #111;
  line-height: 1;
}

.price-old {
  font-size: 0.9rem;
  color: #bbb;
  text-decoration: line-through;
}

.savings-pill {
  font-size: 0.68rem;
  font-weight: 700;
  background: #fff3cd;
  color: #856404;
  padding: 2px 6px;
  border-radius: 20px;
}

.set-pill {
  font-size: 0.68rem;
  font-weight: 700;
  background: #111;
  color: #FFD500;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.2px;
}

/* ---- Excl. BTW ---- */
.excl-btw-row {
  font-size: 0.72rem;
  color: #999;
  margin-top: -4px;
}

/* ---- Op voorraad ---- */
.in-stock-row {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #16a34a;
}

.stock-dot {
  font-size: 0.55rem;
  line-height: 1;
}

/* ---- Levertijd badge ---- */
.delivery-time-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
}

.delivery-time-badge i { font-size: 0.78rem; }

/* ---- Stock warning ---- */
.stock-warning {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #e65100;
  background: #fff3e0;
  padding: 6px 10px;
  border-radius: 6px;
  border-left: 2px solid #ff9800;
}

/* ---- Add to cart ---- */
.btn-add-to-cart {
  width: 100%;
  background: #FFD500;
  color: #111;
  border: none;
  border-radius: 7px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.15s ease, transform 0.15s ease;
}

.btn-add-to-cart:hover:not(:disabled) {
  background: #ffb800;
  transform: translateY(-1px);
}

.btn-add-to-cart:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

/* ---- Responsive ---- */
@media (max-width: 480px) {
  .product-body    { padding: 10px 12px 12px; }
  .product-title   { font-size: 0.85rem; }
  .price-current   { font-size: 1.15rem; }
  .btn-add-to-cart { font-size: 0.8125rem; padding: 9px 12px; }
}

/* ---- Accessibility ---- */
@media (prefers-reduced-motion: reduce) {
  * { animation: none !important; transition: none !important; }
}

button:focus-visible, a:focus-visible {
  outline: 2px solid #FFD500;
  outline-offset: 2px;
}
</style>